import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { Loader2 } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

import { InlineError } from './InlineError';
import { InputField } from './InputField';
import {
  BUTTON_DANGER,
  BUTTON_PRIMARY,
} from '@/shared/ui-class';
import { cn } from '@/shared/cn';

/**
 * Generic confirm dialog (Radix AlertDialog). `ConfirmDelete` is a thin
 * danger-preset wrapper. Supports typed confirmation like the legacy
 * delete-only dialog.
 */
export type ConfirmDialogProps = {
  children?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'default' | 'danger';
  confirmation?: {
    expected: string;
    label: string;
    placeholder?: string;
    caseSensitive?: boolean;
  };
  /** Shown inside the dialog (e.g. when onConfirm rejects and the dialog stays open). */
  error?: ReactNode;
  /** When true the confirm button is disabled regardless of other state. */
  confirmDisabled?: boolean;
  onConfirm: (confirmationValue?: string) => void | Promise<void>;
};

export function ConfirmDialog({
  children,
  open: openProp,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'default',
  confirmation,
  error,
  confirmDisabled = false,
  onConfirm,
}: ConfirmDialogProps) {
  const isControlled = openProp !== undefined;
  const [openInternal, setOpenInternal] = useState(false);
  const open = isControlled ? openProp : openInternal;
  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setOpenInternal(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );
  const [submitting, setSubmitting] = useState(false);
  const [confirmationValue, setConfirmationValue] = useState('');

  useEffect(() => {
    if (!open) setConfirmationValue('');
  }, [open]);

  const confirmationMatches =
    !confirmation ||
    (confirmation.caseSensitive
      ? confirmationValue.trim() === confirmation.expected.trim()
      : confirmationValue.trim().toLowerCase() ===
        confirmation.expected.trim().toLowerCase());

  const handleConfirm = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onConfirm(confirmation ? confirmationValue : undefined);
      setOpen(false);
    } catch {
      // Leave open; caller surfaces the error in its own UI.
    } finally {
      setSubmitting(false);
    }
  }, [onConfirm, setOpen, submitting, confirmation, confirmationValue]);

  return (
    <AlertDialog.Root open={open} onOpenChange={setOpen}>
      {children ? (
        <AlertDialog.Trigger asChild>{children}</AlertDialog.Trigger>
      ) : null}
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <AlertDialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2',
            'rounded-2xl border border-divider bg-white p-6 shadow-lg dark:bg-zinc-950',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          )}
        >
          <AlertDialog.Title className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
            {title}
          </AlertDialog.Title>
          {description ? (
            <AlertDialog.Description className="mt-2 text-sm leading-relaxed text-muted">
              {description}
            </AlertDialog.Description>
          ) : null}

          {confirmation ? (
            <div className="mt-4 space-y-1.5">
              <label className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                {confirmation.label}
              </label>
              <InputField
                value={confirmationValue}
                onChange={(e) => setConfirmationValue(e.target.value)}
                placeholder={confirmation.placeholder}
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          ) : null}

          {error ? <InlineError className="mt-4">{error}</InlineError> : null}

          <div className="mt-5 flex justify-end gap-2">
            <AlertDialog.Cancel asChild>
              <button
                type="button"
                disabled={submitting}
                className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-divider px-3.5 text-sm font-medium text-zinc-600 transition-colors hover:bg-zinc-50 hover:text-zinc-900 disabled:cursor-not-allowed disabled:opacity-50 dark:text-zinc-400 dark:hover:bg-zinc-900 dark:hover:text-zinc-100"
              >
                {cancelLabel}
              </button>
            </AlertDialog.Cancel>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={submitting || !confirmationMatches || confirmDisabled}
              className={cn(
                tone === 'danger' ? BUTTON_DANGER : BUTTON_PRIMARY,
              )}
            >
              {submitting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : null}
              {confirmLabel}
            </button>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
